import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { type PanelSpan } from '@/components/shared';
import type { BackofficeProjectDetail } from '../types';
import { formatEnabledState, formatParticipantRecordingPrivacyMode } from '../format';
import { ProjectDetailPanel } from './ProjectDetailPanelGrid';

export interface ProjectParticipantPrivacyPanelProps {
  project: BackofficeProjectDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  onEdit?: (() => void) | undefined;
}

function ValueChip({
  value,
  tone,
  compact = false,
}: {
  value: string;
  tone: 'neutral' | 'success' | 'info' | 'warning' | 'danger';
  compact?: boolean;
}) {
  const toneClasses = {
    neutral: 'bg-neutral-100 text-neutral-700',
    success: 'bg-emerald-50 text-emerald-700',
    info: 'bg-sky-50 text-sky-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-rose-50 text-rose-700',
  } satisfies Record<string, string>;

  return (
    <span
      className={[
        'inline-flex items-center rounded-full font-semibold',
        compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-caption',
        toneClasses[tone],
      ].join(' ')}
    >
      {value}
    </span>
  );
}

function ValueRow({
  label,
  value,
  chip,
  compact = false,
  dense = false,
}: {
  label: string;
  value: string;
  chip: ReactNode;
  compact?: boolean;
  dense?: boolean;
}) {
  return (
    <div
      className={[
        'border-neutral-100',
        compact
          ? 'rounded-md border bg-neutral-50 px-3 py-1.5'
          : dense
            ? 'flex items-center justify-between gap-2 rounded-md border bg-neutral-50 px-3 py-1.5'
            : 'grid gap-1.5 border-b py-3 first:pt-0 last:border-b-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center',
      ].join(' ')}
    >
      <dt
        className={[
          'font-semibold text-neutral-900',
          compact || dense ? 'text-[10px] uppercase tracking-wide text-neutral-500' : '',
        ].join(' ')}
      >
        {label}
      </dt>
      <dd className={compact ? 'mt-1.5' : 'shrink-0'} aria-label={`${label}: ${value}`}>
        {chip}
      </dd>
    </div>
  );
}

function getParticipantRecordingPrivacyTone(
  value: BackofficeProjectDetail['participantRecordingPrivacyMode']
): 'info' | 'warning' | 'danger' {
  if (value === 'obfuscated') return 'warning';
  if (value === 'hidden') return 'danger';
  return 'info';
}

export function ProjectParticipantPrivacyPanel({
  project,
  panelId,
  currentSpan,
  onEdit,
}: ProjectParticipantPrivacyPanelProps) {
  const isCompactSquare = currentSpan === '1x1';
  const isCompactWide = currentSpan === '2x1';
  const useCompactChips = isCompactSquare || isCompactWide;
  const recordingPrivacyLabel = formatParticipantRecordingPrivacyMode(
    project.participantRecordingPrivacyMode
  );
  const emailCollectionLabel = formatEnabledState(project.collectParticipantEmail);

  return (
    <ProjectDetailPanel
      title="Participant privacy"
      span="2x1"
      panelId={panelId}
      currentSpan={currentSpan}
      contentOverflow={isCompactWide ? 'hidden' : undefined}
      headerActions={
        !project.deletedAt && onEdit ? (
          <button
            type="button"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 transition hover:bg-backoffice-50 hover:text-neutral-700"
            aria-label="Edit participant privacy"
            title="Edit participant privacy"
            onClick={onEdit}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : undefined
      }
    >
      <dl
        className={[
          'flex h-full flex-col',
          isCompactSquare
            ? 'min-h-[7rem] gap-1.5'
            : isCompactWide
              ? 'gap-1.5'
              : 'min-h-[7rem] justify-center',
        ].join(' ')}
      >
        <ValueRow
          label="Interview recording privacy"
          value={recordingPrivacyLabel}
          compact={isCompactSquare}
          dense={isCompactWide}
          chip={
            <ValueChip
              value={recordingPrivacyLabel}
              tone={getParticipantRecordingPrivacyTone(project.participantRecordingPrivacyMode)}
              compact={useCompactChips}
            />
          }
        />
        <ValueRow
          label="Collect participant email"
          value={emailCollectionLabel}
          compact={isCompactSquare}
          dense={isCompactWide}
          chip={
            <ValueChip
              value={emailCollectionLabel}
              tone={project.collectParticipantEmail ? 'success' : 'neutral'}
              compact={useCompactChips}
            />
          }
        />
      </dl>
    </ProjectDetailPanel>
  );
}
